import { formatCurrency, formatDate } from '../lib/format.js';

const TODAY = new Date().toISOString().slice(0, 10);

const STATUS_CONFIG = {
  paid:     { label: 'Payée',     badge: 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200',  row: 'bg-emerald-50/50' },
  overdue:  { label: 'En retard', badge: 'bg-red-50 text-red-700 ring-1 ring-red-200',               row: 'bg-red-50/40' },
  upcoming: { label: 'À venir',   badge: 'bg-slate-50 text-slate-600 ring-1 ring-slate-200',         row: '' },
  grace:    { label: 'Différé',   badge: 'bg-slate-50 text-slate-500 ring-1 ring-slate-200',         row: 'bg-slate-50/60' },
};

/**
 * DebtScheduleTable — tableau d'amortissement complet théorique vs réel.
 *
 * Status par ligne :
 *   - paid      : il existe une tx liée qui correspond chronologiquement à cette échéance
 *                 (logique simple : on alloue les N tx liées aux N premières échéances)
 *   - overdue   : date < today ET pas de tx allouée
 *   - upcoming  : date >= today ET pas de tx allouée
 *
 * Props :
 *   schedule           : [{month_index, date, principal_cents, interest_cents,
 *                          total_cents, capital_remaining_cents}]
 *   linkedTransactions : [...] — utilisé pour déterminer le statut "paid"
 *   maxRows            : number — défaut = Infinity (affiche tout le tableau)
 */
/**
 * Alloue chaque transaction liée à la ligne du schedule la plus proche en
 * date (tolérance ±20 j), en ordre chronologique et sans double-matcher.
 * Retourne un Set<month_index> des lignes payées.
 */
function computePaidIndices(schedule, linkedTransactions) {
  const paid = new Set();
  if (!Array.isArray(linkedTransactions) || !linkedTransactions.length) return paid;

  const usedTx = new Set();
  // Schedule ordonné par date ASC, on ignore les lignes sans paiement prévu (grace)
  const sortedRows = [...schedule]
    .filter((r) => (r.total_cents ?? 0) > 0)
    .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
  const sortedTxs = [...linkedTransactions]
    .filter((t) => t.operation_date || t.settled_at)
    .sort((a, b) => {
      const da = (a.operation_date ?? a.settled_at ?? '');
      const db = (b.operation_date ?? b.settled_at ?? '');
      return da.localeCompare(db);
    });

  for (const row of sortedRows) {
    const rowMs = new Date(row.date + 'T00:00:00').getTime();
    let bestTx = null;
    let bestDiff = Infinity;
    for (const tx of sortedTxs) {
      if (usedTx.has(tx.id)) continue;
      const txDateStr = (tx.operation_date ?? tx.settled_at ?? '').slice(0, 10);
      if (!txDateStr) continue;
      const txMs = new Date(txDateStr + 'T00:00:00').getTime();
      const diffDays = Math.abs((txMs - rowMs) / 86400000);
      if (diffDays <= 20 && diffDays < bestDiff) {
        bestDiff = diffDays;
        bestTx = tx;
      }
    }
    if (bestTx) {
      paid.add(row.month_index);
      usedTx.add(bestTx.id);
    }
  }
  return paid;
}

export default function DebtScheduleTable({ schedule = [], linkedTransactions = [], maxRows = Infinity }) {
  const rows = schedule.slice(0, maxRows);
  const paidIndices = computePaidIndices(schedule, linkedTransactions);

  if (rows.length === 0) {
    return (
      <p className="text-sm text-slate-500 py-4 text-center">Aucune échéance à afficher.</p>
    );
  }

  return (
    <div className="overflow-x-auto -mx-4 md:mx-0">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left text-xs font-medium text-slate-500 px-4 py-2 whitespace-nowrap sticky left-0 bg-white">Date</th>
            <th className="text-right text-xs font-medium text-slate-500 px-4 py-2 whitespace-nowrap">Principal</th>
            <th className="text-right text-xs font-medium text-slate-500 px-4 py-2 whitespace-nowrap">Intérêts</th>
            <th className="text-right text-xs font-medium text-slate-500 px-4 py-2 whitespace-nowrap">Total</th>
            <th className="text-right text-xs font-medium text-slate-500 px-4 py-2 whitespace-nowrap hidden md:table-cell">Capital restant</th>
            <th className="text-center text-xs font-medium text-slate-500 px-4 py-2 whitespace-nowrap">Statut</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row, i) => {
            const dateStr = row.date?.slice(0, 10) ?? '';
            const isPast  = dateStr < TODAY;
            const isGrace = (row.total_cents ?? 0) === 0;
            // Matching par date : une tx liée dont la date est à ±20 j de la
            // ligne théorique est allouée à cette ligne (chronologique, sans
            // double-match). Gère les décalages de convention (start_date vs
            // 1er paiement) et les dérives réelles de quelques jours.
            const isPaid  = paidIndices.has(row.month_index);
            const status  = isGrace
              ? 'grace'
              : (isPaid ? 'paid' : (isPast ? 'overdue' : 'upcoming'));
            const cfg     = STATUS_CONFIG[status];
            return (
              <tr key={row.month_index ?? i} className={`${cfg.row} hover:bg-slate-100/50 transition-colors`}>
                <td className={`px-4 py-2 whitespace-nowrap text-slate-700 sticky left-0 ${cfg.row || 'bg-white'} hover:bg-slate-100/50`}>
                  {formatDate(row.date)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-right tabular-nums text-slate-700">
                  {formatCurrency(row.principal_cents)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-right tabular-nums text-slate-500">
                  {formatCurrency(row.interest_cents)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-right tabular-nums font-medium text-slate-900">
                  {formatCurrency(row.total_cents)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-right tabular-nums text-slate-500 hidden md:table-cell">
                  {formatCurrency(row.capital_remaining_cents)}
                </td>
                <td className="px-4 py-2 text-center">
                  <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${cfg.badge}`}>
                    {cfg.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
